import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListProductInformation from "@theHescoBlueprint/Overrides/Widgets/ProductList/ProductListProductInformation";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 0;
            div[data-test-selector="productListProductImage"] {
                height: 270px;
                align-items: center;
                justify-content: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                height: auto !important;
                                max-height: 270px !important;
                            `,
                            null,
                            css`
                                height: 210px;
                            `,
                            null,
                        ],
                        "max",
                    )};
                a[data-test-selector="productImage"] {
                    max-height: 100%;
                    img {
                        max-height: 270px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        height: auto !important;
                                        max-height: 210px !important;
                                    `,
                                    null,
                                    css`
                                        max-height: 210px;
                                    `,
                                    null,
                                ],
                                "max",
                            )};
                    }
                }
            }
        `,
    },
    actionsWrapper: {
        css: css`
            section {
                border-bottom: 1px solid ${getColor("common.border")};
                padding: 10px 0 !important;
                margin-bottom: 5px;
            }
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
}) => {
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
            <StyledWrapper {...styles.imageWrapper}>
                {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                <ProductListProductImage
                    showImage={showImage}
                    showCompare={showCompare}
                    showImageBadge={showImageBadges}
                    showTextBadge={showTextBadges}
                />
            </StyledWrapper>
            <ProductListProductInformation
                showAttributes={showAttributes}
                showAvailability={showAvailability}
                showBrand={showBrand}
                showPartNumbers={showPartNumbers}
                showTitle={showTitle}
                showCompare={showCompare}
                showImageBadges={showImageBadges}
                maxImageBadges={maximumNumberOfImageBadges}
                showTextBadges={showTextBadges}
                maxTextBadges={maximumNumberOfTextBadges}
            />
            <StyledWrapper {...styles.actionsWrapper}>
                <ProductListActions showAddToList={showAddToList} showPrice={showPrice} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductGridCard);
